import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { type ReactNode, useState } from 'react';
import nextUrl from '../../../assets/dashboard/page-next.svg';
import prevUrl from '../../../assets/dashboard/page-prev.svg';
import { MediaCard } from '../../media/shared/MediaCard';
import { ReportSection } from '../ReportCard';

const PAGE_SIZE = 4;

interface CreativeCarouselProps<T> {
  title: string;
  description: string;
  items: T[];
  empty: string;
  renderItem: (item: T) => ReactNode;
  itemKey: (item: T) => string;
}

interface PageButtonProps {
  icon: string;
  label: string;
  disabled: boolean;
  onClick: () => void;
}

function PageButton({ icon, label, disabled, onClick }: PageButtonProps): ReactNode {
  return (
    <ButtonBase
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      sx={{
        width: 32,
        height: 32,
        borderRadius: '16px',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        '&.Mui-disabled': { opacity: 0.45 },
      }}
    >
      <Box component="img" src={icon} alt="" width={16} height={16} />
    </ButtonBase>
  );
}

/** A report section of creative cards, four per page, with its pager in the section header. */
export function CreativeCarousel<T>({
  title,
  description,
  items,
  empty,
  renderItem,
  itemKey,
}: CreativeCarouselProps<T>): ReactNode {
  const [page, setPage] = useState(0);
  const pages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const current = Math.min(page, pages - 1);
  const start = current * PAGE_SIZE;
  const shown = items.slice(start, start + PAGE_SIZE);

  const pager =
    items.length === 0 ? undefined : (
      <Stack direction="row" sx={{ alignItems: 'center', gap: '8px' }}>
        <Typography sx={{ fontSize: 12, fontWeight: 500, color: 'text.secondary' }}>
          {start + 1}-{start + shown.length} of {items.length}
        </Typography>
        <PageButton
          icon={prevUrl}
          label="Previous creatives"
          disabled={current === 0}
          onClick={() => setPage(current - 1)}
        />
        <PageButton
          icon={nextUrl}
          label="Next creatives"
          disabled={current >= pages - 1}
          onClick={() => setPage(current + 1)}
        />
      </Stack>
    );

  return (
    <ReportSection title={title} description={description} action={pager}>
      {items.length === 0 ? (
        <MediaCard sx={{ p: '32px', textAlign: 'center' }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{empty}</Typography>
        </MediaCard>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              lg: `repeat(${PAGE_SIZE}, minmax(0, 1fr))`,
            },
            gap: '16px',
          }}
        >
          {shown.map((item) => (
            <Box key={itemKey(item)} sx={{ minWidth: 0 }}>
              {renderItem(item)}
            </Box>
          ))}
        </Box>
      )}
    </ReportSection>
  );
}
